import { useState } from "react";

// 8 collapsible secties met de essentiele info uit Tiago Máximo's
// "Buying Land in Portugal — What Nobody Tells You" guide.
// Bron en credit aan @tiago.maximo / maximoproperties.pt.

const SECTIONS = [
  {
    id: "mistakes",
    icon: "⚠️",
    title: "3 grootste fouten (vermijd deze)",
    body: (
      <>
        <p><b>1. Verliefd op het uitzicht, viability vergeten.</b><br />
          Het uitzicht staat niet op de eigendomsakte. Vraag <em>eerst</em> documenten op — pas dan emotie.</p>
        <p><b>2. Ruïne ≠ huis.</b><br />
          Niet elke ruïne mag worden herbouwd. Een hoop stenen zonder registratie heeft <em>geen</em> bouwwaarde.</p>
        <p><b>3. Budget onrealistisch.</b><br />
          Koopprijs is vaak {`< 50%`} van totaal. Reken architect, vergunningen, bouw, infra (water/elek/riool/wegen),
          belastingen (IMT + zegelrecht + registratie) en onverwachte kosten erbij.</p>
        <p className="bc-quote">"Er is geen 'instap-klaar huis voor €50k' na een ruïne. Wie met cijfers van 5 jaar geleden komt, schrikt zich kapot."</p>
      </>
    ),
  },
  {
    id: "docs",
    icon: "📄",
    title: "Documenten-checklist",
    body: (
      <>
        <ul className="bc-list">
          <li>
            <b>Certidão Permanente do Registo Predial</b>
            <small>Het primaire eigendomsbewijs. Online via <a href="https://www.predialonline.pt" target="_blank" rel="noopener">predialonline.pt</a> voor ~€15, 6 maanden geldig. Verifieer dat eigenaar, oppervlak en lasten matchen.</small>
          </li>
          <li>
            <b>Caderneta Predial</b>
            <small>Fiscaal document van de Finanças. Moet exact matchen met de Registo Predial — anders zit er werk in.</small>
          </li>
          <li>
            <b>Certidão de Isenção de Licença de Utilização</b>
            <small>Cruciaal voor oude gebouwen: certificeert dat 't pand vóór 7 aug 1951 is gebouwd (RGEU) en dus geen gebruikslicentie nodig heeft. Datum varieert per gemeente.</small>
          </li>
          <li className="bc-warn">
            <b>⚠️ NIET aanvragen: Certidão de Ruína</b>
            <small>Verklaart het pand "ruïneus" — daarna eist de gemeente vaak een volledig nieuwbouwproject. Vraag i.p.v. een eenvoudige informatie-aanvraag (Pedido de Informação).</small>
          </li>
          <li>
            <b>Certificado Energético</b>
            <small>Ruïnes zijn wettelijk vrijgesteld, maar notarissen vragen vaak om de uitzonderingsverklaring op de dag van de akte. Regel dit ruim van tevoren.</small>
          </li>
          <li>
            <b>PDM consultatie bij gemeente (câmara)</b>
            <small>Wat staat het Plano Director Municipal toe op deze precieze locatie?</small>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "first-visit",
    icon: "🔍",
    title: "Bij eerste bezichtiging vragen",
    body: (
      <>
        <p><b>Over het pand</b></p>
        <ul className="bc-list-compact">
          <li>Water? Bron, put, leiding of beek?</li>
          <li>Elektriciteit aangesloten of zonder excessieve kosten haalbaar?</li>
          <li>Toegangsweg openbaar, of kruist deze land van derden?</li>
          <li>Geregistreerd oppervlak — matcht dit met de werkelijkheid?</li>
          <li>Bestaande structuur — geregistreerd? Conditie?</li>
        </ul>
        <p><b>Over de omgeving</b></p>
        <ul className="bc-list-compact">
          <li>Zon-oriëntatie (raakt comfort + bouwkost)</li>
          <li>Buren-gebruik: intensieve landbouw, toerisme, residentieel?</li>
          <li>Erfdienstbaarheden / rechten van overpad?</li>
        </ul>
        <p><b>Over het proces</b></p>
        <ul className="bc-list-compact">
          <li>Wie is de eigenaar? Zijn er erfgenamen? Eensgezindheid?</li>
          <li>Schulden, lasten, hypotheken op het pand?</li>
          <li>Echte motivatie om te verkopen? (bepaalt onderhandelingsruimte)</li>
        </ul>
      </>
    ),
  },
  {
    id: "neighbours",
    icon: "🏘",
    title: "Buren-factor (massaal onderschat)",
    body: (
      <>
        <p>Een slechte buur kun je niet repareren met geld. Onderzoek dit even serieus als documenten.</p>
        <p><b>Wat je tegen kunt komen in rural gebieden:</b></p>
        <ul className="bc-list-compact">
          <li>Dierenverblijven (kippen, hanen, varkens, vee) — geluid + geur, alle uren</li>
          <li>Tractoren bij dageraad of in 't weekend</li>
          <li>Verbrandingen in verboden periodes of op winderige dagen</li>
          <li>Gedeelde paden zonder rust-uren</li>
          <li>Oude conflicten die je <em>overneemt</em> bij koop</li>
        </ul>
        <p><b>Onderzoeksaanpak:</b></p>
        <ul className="bc-list-compact">
          <li>Bezoek op verschillende dagen + tijden (werkdag-ochtend, late namiddag)</li>
          <li>Loop langs de perimeter — wat ligt direct ernaast?</li>
          <li>Praat met locals — niet voor meningen, voor patroon</li>
          <li>Vraag agent direct: "Zijn er conflicten of issues die ik moet weten?"</li>
          <li>Als 't kan: verschijn onaangekondigd en blijf een uur. Luister + ruik.</li>
        </ul>
      </>
    ),
  },
  {
    id: "agent",
    icon: "🤝",
    title: "Maak de makelaar je bondgenoot",
    body: (
      <>
        <p>De agent weet meer dan op de listing staat. Of je dat hoort, hangt af van hoe je binnenkomt.</p>
        <p><b>Houding die deuren sluit:</b> alwetend, combatief, tegenspreken. → Agent gaat in verdediging, kritische info komt niet naar boven.</p>
        <p><b>Houding die deuren opent:</b> nieuwsgierig, open vragen, "ik kom leren".</p>
        <p><b>De vragen die alles onthullen:</b></p>
        <ul className="bc-list-compact">
          <li><em>"Als jij dit pand zou kopen, wat zou jou het meest zorgen baren?"</em></li>
          <li><em>"Wat hebben eerdere kopers gevraagd dat ik nog niet heb gevraagd?"</em></li>
          <li><em>"Waarom verkoopt de eigenaar? Wat doen ze hierna?"</em></li>
          <li><em>"Hoe lang staat dit al op de markt? Eerdere notering?"</em></li>
          <li><em>"Komt er iets gepland voor dit gebied de komende jaren?"</em></li>
          <li><em>"Hebben de directe buren interesse in dit pand?"</em> (test of pre-emptie speelt)</li>
        </ul>
      </>
    ),
  },
  {
    id: "pre-emption",
    icon: "⚖️",
    title: "Direito de Preferência (Art. 1380 CC)",
    body: (
      <>
        <p className="bc-warn-block">
          <b>De juridische valkuil die de meeste kopers nooit zien.</b><br />
          Aangrenzende landeigenaren mogen jouw aankoop <em>overnemen</em> op dezelfde voorwaarden — zelfs tot 6 maanden NA de akte als ze niet correct genotificeerd zijn.
        </p>
        <p><b>De spelregels:</b></p>
        <ul className="bc-list-compact">
          <li>Aangrenzende buren moeten <em>vóór</em> de akte aangetekend genotificeerd worden</li>
          <li>Ze hebben 8 kalenderdagen om hun voorkeursrecht uit te oefenen</li>
          <li>Geen notificatie = 6 maanden achteraf alsnog te claimen</li>
          <li>Voor stedelijk vastgoed in herstelzones: 10 werkdagen via casapronta.pt</li>
        </ul>
        <p><b>Minimum-grootte (Alentejo Litoral, <em>unidade de cultura</em>):</b></p>
        <table className="bc-table">
          <tbody>
            <tr><td>Geïrrigeerd land (regadio)</td><td><b>4 ha</b></td></tr>
            <tr><td>Droog land / scrubland</td><td><b>48 ha</b></td></tr>
            <tr><td>Bos</td><td><b>48 ha</b></td></tr>
          </tbody>
        </table>
        <p><b>Praktische tip:</b> in Odemira/Aljezur kun je via ABM (Associação de Beneficiários do Mira) verifiëren of een perceel binnen de Mira-irrigatieperimeter valt = waarschijnlijk regadio.</p>
        <p className="bc-quote">"Vraag de agent of de buren interesse hebben — voorkomt 6 maanden later een rechtszaak."</p>
      </>
    ),
  },
  {
    id: "foreigner",
    icon: "🛂",
    title: "Wat heb je als buitenlander nodig?",
    body: (
      <>
        <ul className="bc-list-compact">
          <li><b>NIF</b> (Portugese fiscale identificatie) — verplicht. Via Finanças of via vertegenwoordiger</li>
          <li><b>Geldig paspoort / ID</b></li>
          <li><b>Solicitor of advogado</b> — sterk aanbevolen, eigenlijk niet optioneel</li>
          <li><b>Proof of funds + banking compliance</b> — afhankelijk van land + bank</li>
          <li><b>Portugese bankrekening</b> — wettelijk niet verplicht, praktisch wel handig</li>
        </ul>
        <p className="bc-warn-block">
          <b>Het belangrijkste advies in de hele guide:</b><br />
          Werk altijd met een gekwalificeerde solicitor (solicitador) of advogado. Die verifieert documenten, regelt pre-emptie-notificaties, communiceert met de notaris, en zorgt dat je tekent wat je dénkt te tekenen. Kosten = klein deel van koopprijs. Niet doen = jaren procederen.
        </p>
      </>
    ),
  },
  {
    id: "myths",
    icon: "🌪",
    title: "Wat mensen romantiseren (en de realiteit)",
    body: (
      <>
        <p><b>"Ik koop het en kijk daarna wat kan."</b><br />
          Andersom. Eerst checken wat mag, dan kopen. Anders zit je met land waar je niets op kunt bouwen.</p>
        <p><b>"Elke ruïne wordt een prachthuis."</b><br />
          Je bent gebonden aan wat <em>wettelijk bestaat</em>: oppervlak, volume, toegestane functie. Veel "ruïnes" tellen niet eens als ruïne voor licentie-doeleinden.</p>
        <p><b>"Off-grid leven in de natuur is simpel."</b><br />
          Wegen onderhouden, afstanden afleggen, water/internet zelf regelen. Niet voor iedereen — maar weet 't <em>voor</em> je koopt.</p>
        <p><b>"In Portugal bouwen is goedkoop."</b><br />
          Niet meer. Bouw-, vergunnings- en infrastructuurkosten zijn de laatste jaren fors gestegen.</p>
        <p><b>"Als de agent zegt dat 't kan, dan kan 't."</b><br />
          Gevaarlijke aanname. Alleen de câmara (gemeente) beslist op basis van een ingediend project. Alles daarvoor is indicatie.</p>
        <p className="bc-quote">"Mensen kopen op emotie en ontdekken pas later de technische realiteit. Dan is 't te laat."</p>
      </>
    ),
  },
];

export function BuyingChecklist({ embedded = false }) {
  const [openId, setOpenId] = useState(null);

  return (
    <div className={`bc ${embedded ? "bc-embedded" : ""}`}>
      <div className="bc-intro">
        <p className="bc-tagline">
          Praktische gids — door <a href="https://maximoproperties.pt" target="_blank" rel="noopener">Tiago Máximo</a>,
          specialist Sudoeste Alentejano + Costa Vicentina. Gedestilleerd uit honderden site-visits.
        </p>
        <p className="bc-quote bc-quote-final">
          <b>"Draai de volgorde om. Eerst vragen. Daarna verliefd worden."</b>
        </p>
      </div>

      <div className="bc-sections">
        {SECTIONS.map(sec => (
          <details
            key={sec.id}
            className="bc-section"
            open={openId === sec.id}
            onToggle={(e) => {
              if (e.target.open) setOpenId(sec.id);
              else if (openId === sec.id) setOpenId(null);
            }}
          >
            <summary>
              <span className="bc-section-icon">{sec.icon}</span>
              <span className="bc-section-title">{sec.title}</span>
              <span className="bc-chevron" aria-hidden>▾</span>
            </summary>
            <div className="bc-section-body">{sec.body}</div>
          </details>
        ))}
      </div>

      <p className="bc-disclaimer">
        <small>
          Algemene info uit Tiago Máximo's "Buying Land in Portugal — What Nobody Tells You" (2024).
          Regels variëren per gemeente, zone en perceel. Verifieer altijd met een gekwalificeerde architect, solicitor en/of advogado vóór beslissing.
        </small>
      </p>
    </div>
  );
}
